import type { DocumentLink } from "@/models/document-link/DocumentLink";

/**
 * True when the open document is both {@link DocumentLink.originDocumentId} and
 * {@link DocumentLink.destinationDocumentId}. In our data model this is always an
 * **internal** reference (`ref_type` `internal`); effective fields use the origin side.
 */
export function isSameDocumentBothEnds(
  link: DocumentLink,
  currentDocumentId: string | undefined
): boolean {
  return (
    Boolean(currentDocumentId) &&
    Boolean(link.originDocumentId) &&
    link.originDocumentId === link.destinationDocumentId &&
    link.originDocumentId === currentDocumentId
  );
}

/**
 * Effective HTML element ids (typically on block/div-like nodes) plus disambiguation
 * text for the side of the link that lives on the document currently being rendered.
 * Templates only populate destination*; full links may use origin* or destination*
 * depending on which document is open.
 *
 * **Important:** When `currentDocumentId` matches only one end, fields always come from
 * that end only. Never fall back to the other document’s `htmlIds` / match text (e.g.
 * `text_level_to_document` with an empty `destinationHtmlIds` is document-level on the
 * destination — there is no text anchor on that side; origin ids must not be applied
 * while viewing the destination).
 */
export function getEffectiveDivFields(
  link: DocumentLink,
  currentDocumentId?: string
): {
  htmlIds: string[] | undefined;
  uniqueChunkText: string | undefined;
  uniqueMatchText: string | undefined;
} {
  if (!currentDocumentId) {
    if (link.originHtmlIds && link.originHtmlIds.length > 0) {
      return {
        htmlIds: link.originHtmlIds,
        uniqueChunkText: link.originUniqueChunkText,
        uniqueMatchText: link.originUniqueMatchText,
      };
    }
    return {
      htmlIds: link.destinationHtmlIds,
      uniqueChunkText: link.destinationUniqueChunkText,
      uniqueMatchText: link.destinationUniqueMatchText,
    };
  }

  const onOrigin = link.originDocumentId === currentDocumentId;
  const onDest = link.destinationDocumentId === currentDocumentId;

  if (isSameDocumentBothEnds(link, currentDocumentId)) {
    return {
      htmlIds:
        link.originHtmlIds && link.originHtmlIds.length > 0
          ? link.originHtmlIds
          : undefined,
      uniqueChunkText: link.originUniqueChunkText,
      uniqueMatchText: link.originUniqueMatchText,
    };
  }

  if (onOrigin) {
    return {
      htmlIds:
        link.originHtmlIds && link.originHtmlIds.length > 0
          ? link.originHtmlIds
          : undefined,
      uniqueChunkText: link.originUniqueChunkText,
      uniqueMatchText: link.originUniqueMatchText,
    };
  }

  if (onDest) {
    return {
      htmlIds:
        link.destinationHtmlIds && link.destinationHtmlIds.length > 0
          ? link.destinationHtmlIds
          : undefined,
      uniqueChunkText: link.destinationUniqueChunkText,
      uniqueMatchText: link.destinationUniqueMatchText,
    };
  }

  // Current document id does not match either end (unexpected for by-document queries)
  if (link.originHtmlIds && link.originHtmlIds.length > 0) {
    return {
      htmlIds: link.originHtmlIds,
      uniqueChunkText: link.originUniqueChunkText,
      uniqueMatchText: link.originUniqueMatchText,
    };
  }

  return {
    htmlIds: link.destinationHtmlIds,
    uniqueChunkText: link.destinationUniqueChunkText,
    uniqueMatchText: link.destinationUniqueMatchText,
  };
}
